import { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Badge,
  HStack,
  VStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  useToast,
} from '@chakra-ui/react';
import { FiCheck, FiAlertTriangle, FiCircle, FiEdit2 } from 'react-icons/fi';

import type { MarketCredential } from 'api/quote-marketplace';

interface MarketCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  carriers: MarketCredential[];
  isLoading: boolean;
  error: Error | null;
  syncStatus: 'syncing' | 'ready' | 'error' | undefined;
  syncErrorMessage: string | null;
  credentialErrorCarriers: string[];
  onUpdateCredential: (
    providerCode: string,
    username: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  isUpdating: boolean;
  onRetry: () => void;
}

interface EditingState {
  providerCode: string;
  username: string;
  password: string;
  message: string | null;
  messageType: 'error' | 'success' | null;
}

export const MarketCredentialsModal = ({
  isOpen,
  onClose,
  carriers,
  isLoading,
  error,
  syncStatus,
  syncErrorMessage,
  credentialErrorCarriers,
  onUpdateCredential,
  isUpdating,
  onRetry,
}: MarketCredentialsModalProps) => {
  const [editing, setEditing] = useState<EditingState | null>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const prevStatusRef = useRef<string | undefined>(undefined);
  const toast = useToast();

  const wasUpdatingRef = useRef(false);

  useEffect(() => {
    if (!editing) {
      prevStatusRef.current = undefined;
      wasUpdatingRef.current = false;
      return;
    }

    const carrier = carriers.find(
      (c) => c.provider_code === editing.providerCode,
    );
    if (!carrier) return;

    const currentStatus = carrier.update_status;
    prevStatusRef.current = currentStatus;

    if (currentStatus === 'updating') {
      wasUpdatingRef.current = true;
    }

    if (!wasUpdatingRef.current) return;

    if (currentStatus === 'error') {
      setEditing((prev) =>
        prev
          ? {
              ...prev,
              message: carrier.update_error || 'Update failed',
              messageType: 'error',
              password: '',
            }
          : null,
      );
      return;
    }

    if (currentStatus === 'success') {
      toast({
        title: 'Credentials updated',
        description: `${carrier.name} credentials have been updated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setEditing(null);
    }
  }, [carriers, editing?.providerCode, toast]);

  const handleEdit = (carrier: MarketCredential) => {
    setEditing({
      providerCode: carrier.provider_code,
      username: carrier.username || '',
      password: '',
      message: null,
      messageType: null,
    });
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;

    if (!editing.username.trim()) {
      setEditing({
        ...editing,
        message: 'Username is required',
        messageType: 'error',
      });
      return;
    }

    if (!editing.password.trim()) {
      setEditing({
        ...editing,
        message: 'Password is required',
        messageType: 'error',
      });
      return;
    }

    setEditing({ ...editing, message: null, messageType: null });

    const result = await onUpdateCredential(
      editing.providerCode,
      editing.username,
      editing.password,
    );

    if (!result.success) {
      setEditing({
        ...editing,
        message: result.error || 'Failed to update credentials',
        messageType: 'error',
      });
    }
  };

  const isCarrierUpdating = (carrier: MarketCredential) =>
    carrier.update_status === 'updating';

  const getStatusIcon = (carrier: MarketCredential) => {
    if (isCarrierUpdating(carrier)) {
      return <Spinner size="xs" color="blue.500" />;
    }

    if (carrier.update_status === 'error' || carrier.update_error) {
      return (
        <Badge colorScheme="red" p={1} borderRadius="full">
          <FiAlertTriangle size={12} />
        </Badge>
      );
    }

    if (carrier.has_credential_error) {
      return (
        <Badge colorScheme="orange" p={1} borderRadius="full">
          <FiAlertTriangle size={12} />
        </Badge>
      );
    }

    if (carrier.has_credentials) {
      return (
        <Badge colorScheme="green" p={1} borderRadius="full">
          <FiCheck size={12} />
        </Badge>
      );
    }

    return (
      <Badge colorScheme="gray" p={1} borderRadius="full">
        <FiCircle size={10} />
      </Badge>
    );
  };

  const isEditingCarrier = (providerCode: string) =>
    editing?.providerCode === providerCode;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={1}>Market Credentials</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Your carrier portal credentials
          </Text>
          {isLoading && (
            <Center py={8}>
              <Spinner size="lg" color="brand.500" />
            </Center>
          )}

          {syncStatus === 'error' && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box flex="1">
                <Text fontWeight="medium">Failed to sync credentials</Text>
                {syncErrorMessage && (
                  <Text fontSize="sm">{syncErrorMessage}</Text>
                )}
              </Box>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                onClick={onRetry}
                ml={4}
              >
                Retry
              </Button>
            </Alert>
          )}

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Failed to load market credentials
            </Alert>
          )}

          {!isLoading && !error && carriers.length === 0 && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              No carriers available for this quote
            </Alert>
          )}

          {!isLoading && !error && carriers.length > 0 && (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Carrier</Th>
                    <Th width="50px">Status</Th>
                    <Th width="50px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {carriers.map((carrier) => (
                    <Tr
                      key={carrier.provider_code}
                      opacity={
                        carrier.is_selected_for_rfq ||
                        isEditingCarrier(carrier.provider_code)
                          ? 1
                          : 0.5
                      }
                      bg={carrier.is_selected_for_rfq ? 'white' : 'gray.50'}
                      _hover={{ opacity: 1, bg: 'white' }}
                      transition="opacity 0.15s, background 0.15s"
                    >
                      <Td>
                        <HStack spacing={2}>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">{carrier.name}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {carrier.uses_rateworks
                                ? carrier.provider_name + ' (RateWorks)'
                                : carrier.provider_name}
                            </Text>
                            {carrier.username && (
                              <Text fontSize="xs" color="gray.400">
                                {carrier.username}
                              </Text>
                            )}
                          </VStack>
                          {carrier.is_selected_for_rfq && (
                            <Badge colorScheme="blue" fontSize="2xs">
                              RFQ
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td>{getStatusIcon(carrier)}</Td>
                      <Td>
                        <Popover
                          isOpen={isEditingCarrier(carrier.provider_code)}
                          onClose={handleCancel}
                          placement="left"
                          initialFocusRef={initialFocusRef}
                        >
                          <PopoverTrigger>
                            <IconButton
                              aria-label="Update password"
                              icon={<FiEdit2 />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(carrier)}
                              isDisabled={
                                isCarrierUpdating(carrier) ||
                                (editing !== null &&
                                  !isEditingCarrier(carrier.provider_code))
                              }
                            />
                          </PopoverTrigger>
                          <PopoverContent width="280px">
                            <PopoverArrow />
                            <PopoverBody p={4}>
                              <VStack spacing={3} align="stretch">
                                <FormControl>
                                  <FormLabel fontSize="xs" mb={1}>
                                    Username
                                  </FormLabel>
                                  <Input
                                    ref={initialFocusRef}
                                    size="sm"
                                    variant="main"
                                    value={editing?.username || ''}
                                    onChange={(e) =>
                                      setEditing((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              username: e.target.value,
                                              message: null,
                                            }
                                          : null,
                                      )
                                    }
                                    placeholder="Enter username"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') handleCancel();
                                    }}
                                    isDisabled={isCarrierUpdating(carrier)}
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontSize="xs" mb={1}>
                                    Password
                                  </FormLabel>
                                  <Input
                                    size="sm"
                                    variant="main"
                                    type="password"
                                    value={editing?.password || ''}
                                    onChange={(e) =>
                                      setEditing((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              password: e.target.value,
                                              message: null,
                                            }
                                          : null,
                                      )
                                    }
                                    placeholder="**********"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSave();
                                      if (e.key === 'Escape') handleCancel();
                                    }}
                                    isDisabled={isCarrierUpdating(carrier)}
                                  />
                                  {editing?.message && (
                                    <Text
                                      fontSize="xs"
                                      mt={1}
                                      color={
                                        editing.messageType === 'error'
                                          ? 'red.600'
                                          : 'green.600'
                                      }
                                    >
                                      {editing.message}
                                    </Text>
                                  )}
                                </FormControl>
                                <HStack spacing={2} justify="flex-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleCancel}
                                    isDisabled={
                                      isUpdating || isCarrierUpdating(carrier)
                                    }
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="brand"
                                    onClick={handleSave}
                                    isLoading={
                                      isUpdating || isCarrierUpdating(carrier)
                                    }
                                  >
                                    Save
                                  </Button>
                                </HStack>
                              </VStack>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} size="sm">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
